import { FetchSource, RangeResponse, Source } from 'pmtiles'

export type TileSourceErrorKind = 'timeout' | 'network' | 'server' | 'unknown'

export interface TileSourceError {
  kind: TileSourceErrorKind
  status?: number
  message: string
}

const BAD_RESPONSE_CODE_PATTERN = /Bad response code: (\d+)/

function toTileSourceError(error: unknown): TileSourceError | null {
  if (error instanceof DOMException && error.name === 'AbortError') return null
  if (error instanceof TypeError) return { kind: 'network', message: error.message }
  if (!(error instanceof Error)) return { kind: 'unknown', message: String(error) }

  const status = Number(BAD_RESPONSE_CODE_PATTERN.exec(error.message)?.[1])

  if (status === 504) return { kind: 'timeout', status, message: error.message }
  if (status === 502) return { kind: 'network', status, message: error.message }
  if (status === 400 || status === 403 || status === 500) return { kind: 'server', status, message: error.message }

  return { kind: 'unknown', status: Number.isNaN(status) ? undefined : status, message: error.message }
}

export function createObservedTileSource(url: string, onError: (error: TileSourceError) => void): Source {
  const source = new FetchSource(url)

  return {
    getKey: () => source.getKey(),
    getBytes: async (offset, length, signal, etag): Promise<RangeResponse> => {
      try {
        return await source.getBytes(offset, length, signal, etag)
      } catch (error) {
        const tileSourceError = toTileSourceError(error)
        if (tileSourceError) onError(tileSourceError)
        throw error
      }
    },
  }
}
